import React from "react";
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMetricsUi, headCellSx, numCellSx, textCellSx } from "./metricsUi";
import { formatCount, formatCurrency } from "./metricsFormat";

const skuCountLabel = (count) =>
  `${formatCount(count)} SKU${count === 1 ? "" : "s"} sold`;

// Lines without a product link come back as a SKU fallback row
// (product_id null); label those by SKU so they are not mistaken for products.
const productTitle = (row) => {
  if (row.product_id) return row.name || "Unnamed product";
  return row.sku || "No SKU";
};

const productSubtitle = (row) => {
  if (row.product_id) return skuCountLabel(row.sku_count || 0);
  return row.name
    ? `${row.name} · not linked to a product`
    : "Not linked to a product";
};

/**
 * Inline product breakdown (top products by Gross Sales) for one clicked
 * Daily Detail day.
 * Rendered by the caller inside a colSpan row directly under that day's
 * row — not a separate page section. Content only; no outer table
 * container / scroll region of its own so it stays anchored to the row.
 */
const DailySkuBreakdownTable = ({
  loading = false,
  error = null,
  payload = null,
  onRetry,
}) => {
  const ui = useMetricsUi();

  const products = payload?.products || [];
  const other = payload?.other;
  const showOther = other && other.product_count > 0;

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ ...ui.card, alignItems: "center" }}
        action={
          onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : null
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: ui.colors.textSecondary,
          mb: 0.75,
        }}
      >
        Top 10 Products by Gross Sales
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          color: ui.colors.textTertiary,
          mb: 1,
        }}
      >
        Each product combines all of its SKUs (sizes / colours). Orders are
        distinct orders containing that product and therefore do not add up to
        the day&apos;s Orders.
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx(ui, { backgroundColor: "transparent" })}>
              Rank
            </TableCell>
            <TableCell sx={headCellSx(ui, { backgroundColor: "transparent" })}>
              Product
            </TableCell>
            <TableCell
              sx={headCellSx(ui, {
                backgroundColor: "transparent",
                textAlign: "right",
              })}
            >
              Gross Sales
            </TableCell>
            <TableCell
              sx={headCellSx(ui, {
                backgroundColor: "transparent",
                textAlign: "right",
              })}
            >
              Orders
            </TableCell>
            <TableCell
              sx={headCellSx(ui, {
                backgroundColor: "transparent",
                textAlign: "right",
              })}
            >
              Units Sold
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`sku-skel-${index}`}>
                  <TableCell sx={textCellSx(ui, { py: 0.75 })}>
                    <Skeleton width={20} />
                  </TableCell>
                  <TableCell sx={textCellSx(ui, { py: 0.75 })}>
                    <Skeleton width="60%" />
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                    <Skeleton width={72} sx={{ ml: "auto" }} />
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                    <Skeleton width={40} sx={{ ml: "auto" }} />
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                    <Skeleton width={40} sx={{ ml: "auto" }} />
                  </TableCell>
                </TableRow>
              ))
            : products.map((row) => (
                <TableRow
                  key={`${row.rank}-${row.product_id || row.sku || "blank"}`}
                >
                  <TableCell
                    sx={textCellSx(ui, {
                      color: ui.colors.textTertiary,
                      py: 0.75,
                    })}
                  >
                    {row.rank}
                  </TableCell>
                  <TableCell sx={textCellSx(ui, { py: 0.75 })}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {productTitle(row)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: ui.colors.textTertiary,
                      }}
                    >
                      {productSubtitle(row)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { fontWeight: 600, py: 0.75 })}>
                    {formatCurrency(row.gross_sales)}
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                    {formatCount(row.orders)}
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { py: 0.75 })}>
                    {formatCount(row.units_sold)}
                  </TableCell>
                </TableRow>
              ))}
          {!loading && showOther ? (
            <TableRow>
              <TableCell
                sx={textCellSx(ui, { color: ui.colors.textTertiary, py: 0.75 })}
              >
                —
              </TableCell>
              <TableCell sx={textCellSx(ui, { fontWeight: 600, py: 0.75 })}>
                Other {formatCount(other.product_count)} products
              </TableCell>
              <TableCell sx={numCellSx(ui, { fontWeight: 600, py: 0.75 })}>
                {formatCurrency(other.gross_sales)}
              </TableCell>
              <TableCell
                sx={numCellSx(ui, { color: ui.colors.textTertiary, py: 0.75 })}
              >
                —
              </TableCell>
              <TableCell sx={numCellSx(ui, { fontWeight: 600, py: 0.75 })}>
                {formatCount(other.units_sold)}
              </TableCell>
            </TableRow>
          ) : null}
          {!loading && !products.length ? (
            <TableRow>
              <TableCell
                colSpan={5}
                sx={textCellSx(ui, {
                  textAlign: "center",
                  color: ui.colors.textTertiary,
                  py: 0.75,
                })}
              >
                No product activity for this day
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Box>
  );
};

export default DailySkuBreakdownTable;
